import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Wallet, IndianRupee, CheckCircle, Filter, Download, Search } from 'lucide-react';

const AdminCommissions = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [monthFilter, setMonthFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [commissionsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/commissions${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_URL}/api/admin/stats`, { headers: getAuthHeaders() })
      ]);
      setCommissions(commissionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async (commissionId) => {
    try {
      await axios.put(`${API_URL}/api/admin/commissions/${commissionId}/pay`, null, {
        headers: getAuthHeaders()
      });
      toast.success('Commission marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const uniqueMonths = [...new Set(commissions.map(c => c.month))].sort().reverse();
  const uniquePartners = [...new Set(commissions.map(c => c.partner_name).filter(Boolean))].sort();

  const filteredCommissions = commissions.filter(c => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      c.partner_name.toLowerCase().includes(term) ||
      (c.company_name || '').toLowerCase().includes(term);
    const matchesMonth = monthFilter === 'all' || c.month === monthFilter;
    const matchesPartner = partnerFilter === 'all' || c.partner_name === partnerFilter;
    return matchesSearch && matchesMonth && matchesPartner;
  });

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const exportToCSV = () => {
    const headers = ['Partner', 'Company', 'Month', 'Client', 'Plan Value', 'Commission Rate', 'Amount', 'Status', 'Paid Date'];
    const rows = filteredCommissions.map(c => [
      c.partner_name,
      c.company_name || '',
      formatMonth(c.month),
      c.client_name,
      c.plan_value,
      `${(c.commission_rate * 100).toFixed(0)}%`,
      c.amount,
      c.status,
      c.paid_date || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all-commissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-commissions">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">
              Commission Management
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              Process payouts and manage commission records
            </p>
          </div>
          <Button variant="outline" className="border-[#E2E2DF]" onClick={exportToCSV} data-testid="export-csv">
            <Download className="w-4 h-4 mr-2" /> Export All
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Total Paid</p>
                  <p className="text-xl font-bold text-green-600" data-testid="total-paid">
                    {formatCurrency(stats?.total_commissions_paid || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Pending Payouts</p>
                  <p className="text-xl font-bold text-amber-600" data-testid="pending-payouts">
                    {formatCurrency(stats?.pending_payouts || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Total Transactions</p>
                  <p className="text-xl font-bold text-blue-600" data-testid="total-transactions">
                    {filteredCommissions.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Search by partner or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 border-[#E2E2DF]"
                />
              </div>
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger className="w-44 h-10 border-[#E2E2DF]">
                  <SelectValue placeholder="Partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partners</SelectItem>
                  {uniquePartners.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger className="w-44 h-10 border-[#E2E2DF]">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {uniqueMonths.map(m => (
                    <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 h-10 border-[#E2E2DF]" data-testid="filter-status">
                  <Filter className="w-4 h-4 mr-2 text-[#64748B]" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border-[#E2E2DF] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Partner</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Month</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Client</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Plan Value</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Rate</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Amount</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredCommissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-[#64748B]">
                      No commission records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCommissions.map((commission, index) => (
                    <TableRow key={commission.id} className="hover:bg-[#FAFAF8]/50" data-testid={`commission-row-${index}`}>
                      <TableCell>
                        {/* Person and agency together: the commission is earned by the one and
                            paid to the other, and every row here repeats the same pair. A second
                            line rather than a column keeps a table that already scrolls narrow. */}
                        <p className="font-medium text-[#1A1A1A]">{commission.partner_name}</p>
                        <p className="text-xs text-[#64748B]">{commission.company_name || '—'}</p>
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">{formatMonth(commission.month)}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{commission.client_name}</TableCell>
                      <TableCell className="text-sm text-[#1A1A1A]">{formatCurrency(commission.plan_value)}</TableCell>
                      <TableCell className="text-sm text-[#1A1A1A]">{(commission.commission_rate * 100).toFixed(0)}%</TableCell>
                      <TableCell className="font-medium text-green-600">{formatCurrency(commission.amount)}</TableCell>
                      <TableCell>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          commission.status === 'paid' 
                            ? 'bg-green-100 text-green-700 border border-green-200' 
                            : 'bg-amber-100 text-amber-700 border border-amber-200'
                        }`}>
                          {commission.status === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {commission.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => markAsPaid(commission.id)}
                            className="bg-[#ef785b] hover:bg-[#D44017] text-white"
                            data-testid={`pay-commission-${index}`}
                          >
                            Mark Paid
                          </Button>
                        )}
                        {commission.status === 'paid' && (
                          <span className="text-xs text-[#64748B]">
                            {commission.paid_date ? new Date(commission.paid_date).toLocaleDateString('en-IN') : ''}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminCommissions;
